<template>
  <div v-if="isOpen" class="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center p-4 z-50 animate-modalFade">
    <div class="w-full max-w-md bg-gray-800 text-indigo-200 rounded-lg p-4 sm:p-5 flex flex-col gap-4 relative shadow-lg border border-gray-800 animate-scaleIn">
      <!-- Header -->
      <div class="flex justify-between items-center">
        <h2 class="text-lg font-semibold text-indigo-200">เปลี่ยนชื่อตัวละคร</h2>
        <button
          @click="closeModal"
          class="text-indigo-200 hover:text-indigo-300 transition-colors duration-200"
          :disabled="isSubmitting"
          aria-label="ปิด"
        >
          <i class="fas fa-times text-sm"></i>
        </button>
      </div>

      <!-- Confirm Step -->
      <div v-if="showConfirmation" class="flex flex-col gap-4">
        <div class="bg-gray-900 border border-gray-700 rounded-md p-3 text-center text-xs sm:text-sm">
          คุณแน่ใจหรือไม่ว่าจะเปลี่ยนชื่อเป็น
          <span class="font-semibold text-indigo-300">{{ newName }}</span>
          โดยใช้ <span class="font-semibold text-green-300">50 cash</span> ?
        </div>

        <div class="flex gap-2">
          <button
            @click="showConfirmation = false"
            class="flex-1 bg-gray-800 hover:bg-gray-700 text-white py-2 rounded-md text-xs sm:text-sm transition-colors duration-200 border border-gray-700"
            :disabled="isSubmitting"
          >
            <i class="fas fa-rotate-left mr-1 text-xs"></i> ยกเลิก
          </button>

          <button
            @click="confirmChangeName"
            class="flex-1 bg-indigo-800 hover:bg-indigo-900 text-white py-2 rounded-md text-xs sm:text-sm transition-colors duration-200 border border-indigo-900"
            :disabled="isSubmitting"
          >
            <span v-if="!isSubmitting"><i class="fas fa-check mr-1 text-xs"></i> ยืนยัน</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-indigo-200 mx-auto"></div>
          </button>
        </div>

        <div v-if="errorMessage" class="text-red-300 text-xs text-center">
          {{ errorMessage }}
        </div>
      </div>

      <!-- Main -->
      <template v-else>
        <!-- Wallet / Cost -->
        <div v-if="loading" class="text-center">
          <div class="animate-spin rounded-full h-6 w-6 border-t-2 border-indigo-600 mx-auto"></div>
          <p class="text-xs mt-1">กำลังโหลด...</p>
        </div>

        <div v-else class="bg-gray-900 rounded-md p-2 text-xs sm:text-sm border border-gray-700 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <i class="fas fa-money-bill-wave text-green-400"></i>
            <span>กระเป๋าเงิน: <span class="text-green-300 font-semibold">{{ userCash }}</span></span>
          </div>
          <div class="flex items-center gap-2">
            <i class="fas fa-sack-dollar text-yellow-300"></i>
            <span>ค่าใช้จ่าย: <span class="text-yellow-300 font-semibold">50 Cash</span></span>
          </div>
        </div>

        <!-- Input -->
        <div v-if="!loading" class="flex flex-col gap-2">
          <label for="newName" class="text-xs">ชื่อใหม่</label>
          <input
            id="newName"
            v-model="newName"
            type="text"
            class="w-full p-2 bg-gray-900 border border-gray-700 rounded-md text-indigo-200 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-700 transition-all duration-200"
            placeholder="ป้อนชื่อตัวละครใหม่ (สูงสุด 10 ตัวอักษร)"
            maxlength="10"
            :disabled="isSubmitting"
            @input="validateName"
            @keydown.enter="submitChangeName"
          />
          <p v-if="nameError" class="text-red-300 text-xxs sm:text-xs">{{ nameError }}</p>
        </div>

        <!-- Action -->
        <div v-if="!loading" class="mt-1">
          <button
            @click="submitChangeName"
            class="w-full bg-indigo-800 hover:bg-indigo-900 text-white py-2 rounded-md text-xs sm:text-sm transition-colors duration-200 border border-indigo-900"
            :class="{
              'opacity-60 cursor-not-allowed': isSubmitting || !isValidName || userCash < 50
            }"
            :disabled="isSubmitting || !isValidName || userCash < 50"
            aria-label="เปลี่ยนชื่อตัวละคร"
          >
            <span v-if="!isSubmitting"><i class="fas fa-pen-to-square mr-1 text-xs"></i> เปลี่ยนชื่อตัวละคร</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-indigo-200 mx-auto"></div>
          </button>
          <p v-if="userCash < 50" class="text-red-300 text-xxs sm:text-xs text-center mt-2">เงินไม่เพียงพอ (ต้องใช้ 50 cash)</p>
        </div>

        <!-- Messages -->
        <div v-if="errorMessage" class="text-red-300 text-xs text-center">
          {{ errorMessage }}
        </div>
        <div v-if="successMessage" class="text-green-300 text-xs text-center">
          {{ successMessage }}
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from 'vue';
import { projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';

export default {
  name: 'ChangeNameModal',
  props: {
    isOpen: { type: Boolean, default: false }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();

    const newName = ref('');
    const nameError = ref('');
    const errorMessage = ref('');
    const successMessage = ref('');
    const isSubmitting = ref(false);
    const loading = ref(true);
    const userCash = ref(0);
    const currentCharacterName = ref('');
    const showConfirmation = ref(false);

    // Thai letters + spaces only, and disallow identical with old name, <=10 chars
    const thaiValidWordRegex = /^(?![\u0E01-\u0E2E\s]+$)([\u0E00-\u0E7F\s])+$/;

    const isValidName = computed(() => {
      if (!newName.value) return false;
      if (newName.value.length > 10) return false;
      return thaiValidWordRegex.test(newName.value);
    });

    const validateName = () => {
      nameError.value = '';
      if (!newName.value) {
        nameError.value = 'กรุณาป้อนชื่อ';
      } else if (!thaiValidWordRegex.test(newName.value)) {
        nameError.value = 'ชื่อต้องใช้ตัวอักษรไทยเท่านั้น';
      } else if (newName.value.length > 10) {
        nameError.value = 'ชื่อต้องไม่เกิน 10 ตัวอักษร';
      } else if (newName.value === currentCharacterName.value) {
        nameError.value = 'ชื่อใหม่ต้องไม่เหมือนชื่อเดิม';
      }
    };

    const fetchUserData = async () => {
      if (!user.value) {
        errorMessage.value = 'กรุณาล็อกอินก่อน';
        loading.value = false;
        return;
      }

      playerStore.init(user.value.uid);

      try {
        const userData = playerStore.userData;
        const characterData = playerStore.characterData;
        if (userData) userCash.value = userData.cash || 0;
        else errorMessage.value = 'ไม่พบข้อมูลผู้ใช้';

        if (characterData) currentCharacterName.value = characterData.characterName || '';
        else errorMessage.value = 'ไม่พบข้อมูลตัวละคร';
      } catch (err) {
        errorMessage.value = 'ไม่สามารถโหลดข้อมูลได้';
      } finally {
        loading.value = false;
      }
    };

    const closeModal = () => {
      if (isSubmitting.value) return;
      newName.value = '';
      nameError.value = '';
      errorMessage.value = '';
      successMessage.value = '';
      showConfirmation.value = false;
      emit('close');
    };

    const submitChangeName = () => {
      validateName();
      if (!isValidName.value) return;
      if (userCash.value < 50) {
        errorMessage.value = 'เงินไม่เพียงพอ (ต้องใช้ 50 cash)';
        return;
      }
      if (newName.value === currentCharacterName.value) return;
      showConfirmation.value = true;
    };

    const confirmChangeName = async () => {
      isSubmitting.value = true;
      errorMessage.value = '';
      successMessage.value = '';
      try {
        const changeNameFunction = projectFunctions.httpsCallable('gameActions');
        const result = await changeNameFunction({ action: 'characterActions', subAction: 'changeCharacterName', newName: newName.value });
        // simulate immediate local update
        userCash.value = Math.max(0, (userCash.value || 0) - 50);
        currentCharacterName.value = newName.value;
        successMessage.value = result?.data?.message || 'เปลี่ยนชื่อสำเร็จ!';
        showConfirmation.value = false;
        newName.value = '';
        setTimeout(() => closeModal(), 1500);
      } catch (err) {
        errorMessage.value = err?.message || 'เกิดข้อผิดพลาดในการเปลี่ยนชื่อ';
        showConfirmation.value = false;
      } finally {
        isSubmitting.value = false;
      }
    };

    // open/close lifecycle
    watch(
      () => props.isOpen,
      (open) => {
        if (open) {
          loading.value = true;
          fetchUserData();
        }
      }
    );

    onMounted(() => { if (props.isOpen) fetchUserData(); });

    return {
      newName,
      nameError,
      errorMessage,
      successMessage,
      isSubmitting,
      loading,
      userCash,
      currentCharacterName,
      showConfirmation,
      isValidName,
      validateName,
      closeModal,
      submitChangeName,
      confirmChangeName
    };
  }
};
</script>

<style scoped>
/* scrollbar not needed here; keep core animations consistent with team */
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }

@keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }

button:disabled { opacity: 0.6; cursor: not-allowed; }
button:not(:disabled):active { transform: scale(0.98); }

/* tiny helper for very very small text (tailwind v2 ไม่มี text-xxs) */
.text-xxs { font-size: 0.7rem; line-height: 1rem; }
</style>